/* eslint-disable react-refresh/only-export-components -- the audio provider intentionally co-locates its consumer hook. */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '../contexts/Auth0Context.jsx';
import { useAudioStream } from './audioStreamContext.jsx';
import apiService from '../services/apiService.js';
import { useUserAudioQueue, createAudioSession } from '../hooks/useUserAudioQueue.js';

const UserAudioQueueContext = createContext(null);

/**
 * Single audio queue provider shared by DM (App.jsx) and player (PlayerPage.jsx).
 *
 * Eliminates duplicated wiring — each page destructures from context instead
 * of calling useUserAudioQueue directly.
 *
 * BOU-1324: this provider owns the playback session. Audio state lives on a
 * provider-held session object (no module globals), so it is scoped to this
 * mount: it is torn down when the user changes (logout / account switch) and on
 * unmount (route exit). Because the two provider mounts (DM, player) are on
 * mutually-exclusive routes, exactly one session is ever live and all consumers
 * under it share one queue + one <audio> element. Campaign changes within a
 * route are handled by the in-hook scope reset on fetch — keying is
 * (user_id, campaignId), never user_id alone.
 */
export const UserAudioQueueProvider = ({ children }) => {
  const { user } = useAuth();
  const audioStream = useAudioStream();

  // BOU-1324: one playback session per provider mount, held in a ref so its
  // identity is stable across renders and the hook reads/writes it in place.
  const audioSessionRef = useRef(null);
  if (audioSessionRef.current === null) {
    audioSessionRef.current = createAudioSession();
  }

  // Card-boundary gating state (DM sets these via triggerCardAudioFetch)
  const [currentCardIndex, setCurrentCardIndex] = useState(null);
  const [autoCardAdvancement, setAutoCardAdvancement] = useState(true);
  // BOU-751: views (CardPlaybackNarrativeView, QuestConversationOverlay)
  // call setHasMore from their useCardPlayback state. While true, the
  // audio queue polls when empty so a card whose TTS finishes after the
  // queue drains still auto-plays without depending on a socket event.
  const [hasMore, setHasMore] = useState(false);

  // Inc-2: client-authoritative cursor wiring.
  // The PlaybackDriver in PlayerViewV2 registers its onSegmentEnded callback here
  // so the audio queue can call it when all chunks of a request finish playing.
  // Using a ref so the stable callback passed to useUserAudioQueue always reads
  // the latest registered function without recreating the queue hook.
  const onRequestCompleteCallbackRef = useRef(null);
  const onRequestComplete = useCallback((payload) => {
    if (onRequestCompleteCallbackRef.current) {
      onRequestCompleteCallbackRef.current(payload);
    }
  }, []);
  const setOnRequestComplete = useCallback((fn) => {
    onRequestCompleteCallbackRef.current = fn ?? null;
  }, []);

  // Reveal-on-audio-start: lets the view register a callback fired when a new
  // card's audio STARTS playing (drives the driver reveal).
  const onCardAudioStartCallbackRef = useRef(null);
  const onCardAudioStart = useCallback((payload) => {
    if (onCardAudioStartCallbackRef.current) {
      onCardAudioStartCallbackRef.current(payload);
    }
  }, []);
  const setOnCardAudioStart = useCallback((fn) => {
    onCardAudioStartCallbackRef.current = fn ?? null;
  }, []);

  const {
    fetchUserAudioQueue,
    stopQueuePlayback,
    setPlaybackSuppressed,
    teardownAudioSession,
    audioBlocked,
    unlockAudio,
    isPlaying: isAudioQueuePlaying,
  } = useUserAudioQueue({
    user,
    audioStream,
    apiService,
    onRequestComplete,
    onCardAudioStart,
    currentCardIndex,
    autoCardAdvancement,
    hasMore,
    audioSession: audioSessionRef.current,
  });

  // BOU-1324: reset the session when the authenticated user changes (logout /
  // account switch without a full reload) so the prior user's queue + <audio>
  // can never bleed into the next user. Skips the initial mount (no prior user).
  const prevUserIdRef = useRef(undefined);
  useEffect(() => {
    const userId = user?.user_id || user?.email || null;
    if (prevUserIdRef.current !== undefined && prevUserIdRef.current !== userId) {
      // The mounted player shell may still own a viewer/scene/cutscene hold.
      // Reset the departed user's queue without releasing that UI-owned gate.
      teardownAudioSession({ preservePlaybackSuppression: true });
    }
    prevUserIdRef.current = userId;
  }, [user, teardownAudioSession]);

  // BOU-1324: tear the session down on unmount (route exit / StrictMode) so a
  // discarded provider can't leave audio playing.
  useEffect(
    () => () => teardownAudioSession({ closeScheduler: true }),
    [teardownAudioSession],
  );

  const isAudioPlaying = Boolean(audioStream?.isStreaming) || isAudioQueuePlaying;

  // Helper that updates card-boundary state and fetches in one call
  const triggerCardAudioFetch = useCallback((campaignId, cardIndex, autoAdvancement) => {
    if (typeof cardIndex === 'number') {
      setCurrentCardIndex(cardIndex);
    }
    if (typeof autoAdvancement === 'boolean') {
      setAutoCardAdvancement(autoAdvancement);
    }
    fetchUserAudioQueue(campaignId, {
      cardIndex,
      autoCardAdvancement: autoAdvancement,
      // Card advancement is part of the current narration lifecycle. If a
      // combat round installed an exact scope, keep it across every card/segment.
      preserveAudioScope: true,
    });
  }, [fetchUserAudioQueue]);

  const value = useMemo(() => ({
    fetchUserAudioQueue,
    stopQueuePlayback,
    setPlaybackSuppressed,
    audioBlocked,
    unlockAudio,
    isAudioPlaying,
    isAudioQueuePlaying,
    triggerCardAudioFetch,
    setHasMore,
    // Inc-2: registers the PlaybackDriver's onSegmentEnded as the audio-complete
    // callback so the client cursor advances locally on audio completion.
    setOnRequestComplete,
    setOnCardAudioStart,
  }), [
    fetchUserAudioQueue,
    stopQueuePlayback,
    setPlaybackSuppressed,
    audioBlocked,
    unlockAudio,
    isAudioPlaying,
    isAudioQueuePlaying,
    triggerCardAudioFetch,
    setHasMore,
    setOnRequestComplete,
    setOnCardAudioStart,
  ]);

  return (
    <UserAudioQueueContext.Provider value={value}>
      {children}
    </UserAudioQueueContext.Provider>
  );
};

export const useUserAudioQueueContext = () => {
  const context = useContext(UserAudioQueueContext);
  if (!context) {
    throw new Error('useUserAudioQueueContext must be used within UserAudioQueueProvider');
  }
  return context;
};

export default UserAudioQueueContext;
